import { useEffect, useState } from "react";
import { fetchDashboard } from "@/api/burnoff";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const TEAM_SLUG = "kutsa";
const ADMIN_TOKEN_KEY = "burnoff_admin_token";

const BURNOUT_STYLES = {
  Low: "bg-stone-100 text-stone-700",
  Moderate: "bg-amber-50 text-amber-800",
  Elevated: "bg-orange-50 text-orange-800",
  High: "bg-orange-50 text-orange-800",
  Critical: "bg-rose-50 text-rose-800",
};

const LEADER_ACTIONS = {
  Low: [
    "Maintain current workload rhythm",
    "Encourage continued check-ins",
  ],
  Moderate: [
    "Review workload distribution",
    "Open space for candid conversation",
  ],
  High: [
    "Reduce non-critical commitments",
    "Schedule focused well-being check-in",
    "Reassess sprint pressure",
  ],
  Elevated: [
    "Reduce non-critical commitments",
    "Schedule focused well-being check-in",
    "Reassess sprint pressure",
  ],
  Critical: [
    "Reduce non-critical commitments",
    "Schedule focused well-being check-in",
    "Reassess sprint pressure",
  ],
};

const URGENCY_TAG = {
  Low: { label: "No immediate action", className: "bg-stone-100 text-stone-600" },
  Moderate: { label: "Worth attention", className: "bg-amber-50/80 text-amber-800" },
  High: { label: "Prioritise support", className: "bg-orange-50/80 text-orange-800" },
  Elevated: { label: "Prioritise support", className: "bg-orange-50/80 text-orange-800" },
  Critical: { label: "Prioritise support", className: "bg-rose-50/80 text-rose-800" },
};

function getSystemStatusSummary(risk) {
  switch (risk) {
    case "Low":
      return "The team system is stable.";
    case "Moderate":
      return "The team system is stable but showing moderate strain.";
    case "High":
    case "Elevated":
    case "Critical":
      return "The team system is under strain.";
    default:
      return "The team system status is based on this week’s signals.";
  }
}

function getSituationSnapshot(risk, avgEnergy, avgStress) {
  const e = typeof avgEnergy === "number" ? avgEnergy.toFixed(1) : "-";
  const s = typeof avgStress === "number" ? avgStress.toFixed(1) : "-";
  switch (risk) {
    case "Low":
      return `Energy (${e}) and stress (${s}) are in a healthy range. The team rhythm is sustainable.`;
    case "Moderate":
      return `Stress or energy signals suggest a need for attention. Consider small adjustments to workload or support.`;
    case "High":
    case "Elevated":
    case "Critical":
      return `The team is showing signs of strain. Prioritising rest and reducing pressure will help.`;
    default:
      return `Energy (${e}) and stress (${s}) inform this week’s picture. Use the insight above to guide next steps.`;
  }
}

function getStoredToken() {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

function setStoredToken(token) {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
}

function clearStoredToken() {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needAccess, setNeedAccess] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [accessCode, setAccessCode] = useState("");

  const loadDashboard = (adminKey) => {
    setLoading(true);
    setError(null);
    setAccessError(null);
    fetchDashboard(TEAM_SLUG, adminKey)
      .then((res) => {
        setData(res);
        setNeedAccess(false);
        setStoredToken(adminKey);
      })
      .catch((e) => {
        if (e?.status === 403) {
          clearStoredToken();
          setNeedAccess(true);
          setAccessError("Leader access required. Please check your code.");
        } else {
          setError(e?.message || "Failed to load dashboard");
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const token = getStoredToken();
    if (token) {
      loadDashboard(token);
    } else {
      setNeedAccess(true);
      setLoading(false);
    }
  }, []);

  const handleAccessSubmit = (e) => {
    e.preventDefault();
    const code = accessCode.trim();
    if (!code) return;
    setLoading(true);
    setAccessError(null);
    fetchDashboard(TEAM_SLUG, code)
      .then((res) => {
        setData(res);
        setNeedAccess(false);
        setStoredToken(code);
        setAccessCode("");
      })
      .catch((e) => {
        if (e?.status === 403) {
          setAccessError("Leader access required. Please check your code.");
        } else {
          setError(e?.message || "Failed to load dashboard");
        }
      })
      .finally(() => setLoading(false));
  };

  if (loading && !data) {
    return (
      <div className="mx-auto w-full min-w-0 max-w-2xl space-y-6 sm:space-y-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-stone-800 sm:text-2xl">
            Dashboard
          </h1>
          <p className="mt-2 text-sm text-stone-500 leading-relaxed sm:text-base">
            Team insights and reflection.
          </p>
        </div>
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-stone-500">Loading…</p>
        </div>
      </div>
    );
  }

  if (needAccess && !data) {
    return (
      <div className="mx-auto w-full min-w-0 max-w-2xl space-y-6 sm:space-y-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-stone-800 sm:text-2xl">
            Dashboard
          </h1>
          <p className="mt-2 text-sm text-stone-500 leading-relaxed sm:text-base">
            Team insights and reflection.
          </p>
        </div>

        <Card className="min-w-0 overflow-hidden rounded-xl border-stone-200/50 bg-card">
          <CardContent className="p-6 sm:p-8 md:p-10">
            <h2 className="text-center text-lg font-medium text-stone-800 sm:text-xl">
              Leader Access
            </h2>
            <p className="mt-2 text-center text-sm text-stone-500">
              Enter access code to view team insights.
            </p>
            <form onSubmit={handleAccessSubmit} className="mt-6 space-y-4">
              <input
                type="password"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Access code"
                autoComplete="off"
                className="w-full rounded-xl border border-stone-200/80 bg-stone-50/50 px-4 py-3 text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-300 focus:ring-offset-1"
              />
              {accessError && (
                <p className="text-center text-sm text-stone-500">
                  {accessError}
                </p>
              )}
              <Button
                type="submit"
                size="lg"
                className="h-12 w-full"
                disabled={loading || !accessCode.trim()}
              >
                {loading ? "Checking…" : "Continue"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="mx-auto w-full min-w-0 max-w-2xl space-y-6 sm:space-y-8">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-stone-800 sm:text-2xl">
            Dashboard
          </h1>
          <p className="mt-2 text-sm text-stone-500 leading-relaxed sm:text-base">
            Team insights and reflection.
          </p>
        </div>
        <Card className="min-w-0 overflow-hidden border-stone-200/60">
          <CardContent className="p-6 sm:p-8">
            <p className="text-center text-sm text-stone-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const hasData =
    data &&
    (data.participationRate > 0 || (data.insight && data.insight.executiveSummary));
  const emotionalEntries = data?.emotionalDistribution
    ? Object.entries(data.emotionalDistribution).filter(([, count]) => count != null)
    : [];
  const maxEmotionalCount = emotionalEntries.length
    ? Math.max(...emotionalEntries.map(([, c]) => Number(c)), 1)
    : 1;

  return (
    <div className="mx-auto w-full min-w-0 max-w-2xl space-y-6 sm:space-y-8">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-stone-800 sm:text-2xl">
          Dashboard
        </h1>
        <p className="mt-2 text-sm text-stone-500 leading-relaxed sm:text-base">
          Team insights and reflection.
        </p>
        {hasData && (
          <p className="mt-3 text-stone-600 sm:text-base">
            {getSystemStatusSummary(data.insight?.burnoutRiskLevel ?? "Low")}
          </p>
        )}
      </div>

      {!hasData ? (
        <Card className="min-w-0 overflow-hidden border-stone-200/50 bg-card">
          <CardContent className="py-16 text-center">
            <p className="text-stone-500">No reflections yet this week.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Section 1 - Team Pulse */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
            <Card className="min-w-0 overflow-hidden rounded-xl border-stone-200/60 bg-stone-50/50">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-medium uppercase tracking-wide text-stone-400 sm:text-sm">
                  Avg Energy
                </p>
                <p className="mt-2 text-3xl font-semibold tracking-tight text-stone-800 sm:text-4xl">
                  {typeof data.avgEnergy === "number" ? data.avgEnergy.toFixed(1) : "-"}
                </p>
              </CardContent>
            </Card>
            <Card className="min-w-0 overflow-hidden rounded-xl border-stone-200/60 bg-stone-50/50">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-medium uppercase tracking-wide text-stone-400 sm:text-sm">
                  Avg Stress
                </p>
                <p className="mt-2 text-3xl font-semibold tracking-tight text-stone-800 sm:text-4xl">
                  {typeof data.avgStress === "number" ? data.avgStress.toFixed(1) : "-"}
                </p>
              </CardContent>
            </Card>
            <Card className="min-w-0 overflow-hidden rounded-xl border-stone-200/60 bg-stone-50/50">
              <CardContent className="p-5 sm:p-6">
                <p className="text-xs font-medium uppercase tracking-wide text-stone-400 sm:text-sm">
                  Participation
                </p>
                <p className="mt-2 text-3xl font-semibold tracking-tight text-stone-800 sm:text-4xl">
                  {typeof data.participationRate === "number"
                    ? `${(data.participationRate * 100).toFixed(0)}%`
                    : "-"}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Section 2 - Emotional Distribution */}
          {emotionalEntries.length > 0 && (
            <Card className="min-w-0 overflow-hidden rounded-xl border-stone-200/60 bg-card">
              <CardContent className="p-5 sm:p-6">
                <h2 className="text-base font-medium text-stone-800 sm:text-lg">
                  Emotional signals
                </h2>
                <div className="mt-4 space-y-3">
                  {emotionalEntries.map(([state, count]) => {
                    const value = Number(count) || 0;
                    const width = Math.round((value / maxEmotionalCount) * 100);
                    return (
                      <div key={state} className="flex items-center gap-3">
                        <span className="w-28 shrink-0 text-sm text-stone-600 sm:w-32">
                          {state}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="h-6 overflow-hidden rounded-md bg-stone-100">
                            <div
                              className="h-full rounded-md bg-stone-300/80 transition-[width] duration-300"
                              style={{ width: `${Math.max(width, 2)}%` }}
                            />
                          </div>
                        </div>
                        <span className="w-8 shrink-0 text-right text-sm tabular-nums text-stone-500">
                          {value}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Section 3 - Burnout Risk */}
          {data.insight?.burnoutRiskLevel && (
            <Card className="min-w-0 overflow-hidden rounded-xl border-stone-200/60 bg-card">
              <CardContent className="p-5 sm:p-6">
                <h2 className="text-base font-medium text-stone-800 sm:text-lg">
                  Burnout risk
                </h2>
                <div className="mt-3">
                  <span
                    className={`inline-block rounded-full px-3 py-1.5 text-sm font-medium ${
                      BURNOUT_STYLES[data.insight.burnoutRiskLevel] ?? "bg-stone-100 text-stone-700"
                    }`}
                  >
                    {data.insight.burnoutRiskLevel}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Section 4 - Insight Summary */}
          {data.insight?.executiveSummary && (
            <Card className="min-w-0 overflow-hidden rounded-xl border-stone-200/60 bg-stone-50/30">
              <CardContent className="p-5 sm:p-6 md:p-8">
                <h2 className="text-base font-medium text-stone-800 sm:text-lg">
                  System interpretation
                </h2>
                <p className="mt-4 text-stone-600 leading-relaxed sm:text-base">
                  {data.insight.executiveSummary}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Section 5 - Team system status */}
          <Card className="min-w-0 overflow-hidden rounded-xl border-stone-200/60 bg-stone-50/30">
            <CardContent className="p-5 sm:p-6 md:p-8">
              <h2 className="text-base font-medium text-stone-800 sm:text-lg">
                Team system status
              </h2>

              <div className="mt-4 space-y-5">
                <div>
                  <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide">
                    Situation snapshot
                  </h3>
                  <p className="mt-2 text-stone-600 leading-relaxed sm:text-base">
                    {getSituationSnapshot(
                      data.insight?.burnoutRiskLevel ?? "Low",
                      data.avgEnergy,
                      data.avgStress
                    )}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wide">
                    Recommended adjustments
                  </h3>
                  <ul className="mt-2 list-inside list-disc space-y-1.5 text-stone-600 sm:text-base">
                    {(LEADER_ACTIONS[data.insight?.burnoutRiskLevel] ?? LEADER_ACTIONS.Low).map(
                      (action, i) => (
                        <li key={i} className="leading-relaxed">
                          {action}
                        </li>
                      )
                    )}
                  </ul>
                </div>

                <div>
                  <span
                    className={`inline-block rounded-full px-3 py-1.5 text-xs font-medium ${
                      URGENCY_TAG[data.insight?.burnoutRiskLevel]?.className ??
                      URGENCY_TAG.Low.className
                    }`}
                  >
                    {URGENCY_TAG[data.insight?.burnoutRiskLevel]?.label ?? URGENCY_TAG.Low.label}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
